<template>
    <div class="draggable-list" role="list" :data-draggable="draggable ? 'true' : 'false'">
        <div
            v-for="(item, index) in orderedItems"
            :key="resolveItemKey(item, index)"
            class="draggable-list__row"
            :class="{
                'draggable-list__row--dragging': dragFromIndex === index,
                'draggable-list__row--drag-over': dragOverIndex === index && dragFromIndex !== index,
                'draggable-list__row--no-handle': !draggable,
            }"
            @dragover.prevent="onDragOver(index)"
            @dragleave="onDragLeave(index)"
            @drop.prevent="onDrop(index)"
            @mouseenter="onMouseEnter(index)"
            @mouseleave="onMouseLeave(index)"
        >
            <button
                v-if="draggable"
                v-show="visibleHandleIndex === index"
                type="button"
                class="draggable-list__handle"
                draggable="true"
                :title="handleTitle"
                @dragstart="onDragStart(index, $event)"
                @dragend="onDragEnd"
            >
                <slot name="handle">
                    <DragHandleIconSvg />
                </slot>
            </button>
            <div class="draggable-list__content">
                <slot :item="item" :index="index" />
            </div>
        </div>
    </div>
</template>

<script>
import DragHandleIconSvg from '@/assets/icons/drag-handle.svg';

export default {
    name: 'DraggableList',
    components: {
        DragHandleIconSvg,
    },
    props: {
        items: {
            type: Array,
            required: true,
        },
        itemKey: {
            type: [String, Function],
            default: 'id',
        },
        draggable: {
            type: Boolean,
            default: true,
        },
        handleTitle: {
            type: String,
            default: 'Reordenar',
        },
    },
    data() {
        return {
            orderedItems: [],
            dragFromIndex: null,
            dragOverIndex: null,
            visibleHandleIndex: null,
        };
    },
    watch: {
        items: {
            handler() {
                this.$nextTick(() => {
                    this.syncOrderedItems();
                });
            },
            immediate: true,
            deep: true,
        },
    },
    created() {
        this.syncOrderedItems();
    },
    methods: {
        onMouseEnter(index) {
            this.visibleHandleIndex = index;
        },
        onMouseLeave(index) {
            this.visibleHandleIndex = null;
        },
        resolveItemKey(item, index) {
            const key = this.getItemKey(item);
            return key != null ? key : index;
        },

        getItemKey(item) {
            if (typeof this.itemKey === 'function') {
                return this.itemKey(item);
            }

            return String(this.itemKey)
                .split('.')
                .reduce((value, segment) => value?.[segment], item);
        },

        syncOrderedItems() {
            const nextItems = [...this.items];
            const nextByKey = Object.fromEntries(
                nextItems.map(item => [this.getItemKey(item), item])
            );

            if (!this.orderedItems.length) {
                this.orderedItems = nextItems;
                return;
            }

            const ordered = this.items
                .filter(item => nextByKey[this.getItemKey(item)] != null)
                .map(item => nextByKey[this.getItemKey(item)]);

            for (const item of nextItems) {
                const key = this.getItemKey(item);
                if (!ordered.find(entry => this.getItemKey(entry) === key)) {
                    ordered.push(item);
                }
            }

            this.orderedItems = ordered;
        },

        emitReorder(items) {
            this.orderedItems = items;
            this.$emit('reorder', items);
            this.$emit('input', items);
        },

        onDragStart(index, event) {
            this.dragFromIndex = index;
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', String(index));

            const row = event.currentTarget?.closest('.draggable-list__row');
            if (row) {
                event.dataTransfer.setDragImage(row, 16, 16);
            }
        },

        onDragOver(index) {
            if (this.dragFromIndex === null || this.dragFromIndex === index) {
                return;
            }
            this.dragOverIndex = index;
        },

        onDragLeave(index) {
            if (this.dragOverIndex === index) {
                this.dragOverIndex = null;
            }
        },

        onDrop(index) {
            const fromIndex = this.dragFromIndex;
            this.onDragEnd();

            if (fromIndex === null || fromIndex === index) {
                return;
            }

            const items = [...this.orderedItems];
            const [moved] = items.splice(fromIndex, 1);
            items.splice(index, 0, moved);
            this.emitReorder(items);
        },

        onDragEnd() {
            this.dragFromIndex = null;
            this.dragOverIndex = null;
        },
    },
};
</script>

<style scoped>
.draggable-list {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
}

.draggable-list[data-draggable="false"] {
    gap: 0;
}

.draggable-list__row {
    display: flex;
    align-items: flex-start;
    position: relative;
    /* gap: var(--spacing-xs); */
}

.draggable-list__row--dragging {
    opacity: 0.45;
}

.draggable-list__row--drag-over .draggable-list__content {
    outline: 1.5px dashed var(--color-primary);
    outline-offset: 2px;
    /* border-radius: var(--radius-sm); */
}

.draggable-list__row--no-handle .draggable-list__content {
    flex: 1;
    min-width: 0;
}

.draggable-list__handle {
    flex-shrink: 0;
    position: absolute;
    left: calc(-15px);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    align-self: stretch;
    padding: 0;
    margin-top: 5px;
    border: none;
    background: transparent;
    color: var(--color-dark-gray);
    cursor: grab;
    touch-action: none;
    opacity: 0;
    transition: opacity 0.2s ease;
    z-index: 1;
}

.draggable-list__row:hover .draggable-list__handle {
    opacity: 1;
}

.draggable-list__handle:hover {
    color: var(--color-primary);
    scale: 1.15;
}

.draggable-list__handle:active {
    cursor: grabbing;
}

.draggable-list__handle :deep(svg) {
    width: 15px;
    height: 15px;
    pointer-events: none;
    translate: -2px 0;
}

.draggable-list__content {
    flex: 1;
    min-width: 0;
}
</style>
